import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BoxProps, Box, Flex, HStack, Icon, Button } from '@chakra-ui/react';
import { ChevronRightIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import tinycolor from 'tinycolor2';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { CustomText } from '../atoms';
import { getIconFromString } from './MenuCard';
import { useSettings } from '../../stateManagement/SettingsContext';
import { appInsights } from '../../appInsights';
import { SectionIcon } from '../atoms/SectionIcon';

type SectionCardProps = BoxProps & {
  section_id?: string;
  sectionName: string;
  icon: string;
  iconType?: string;
  bgColor?: string;
  textColor?: string;
  faqs: {
    section_id?: any;
    id?: string;
    publish_id: string;
    faq_title: string;
  }[];
  onClickMore: (formattedSectionTitle: string) => void;
  sectionHelperText: string;
  helperTextColor?: string;
  primaryAccentColor?: string;
  secondaryAccentColor?: string;
  categoriesHeadingTextColor?: string;
  moreClickHref?: string;
};

export const SectionCard: React.FC<SectionCardProps> = ({
  section_id,
  sectionName,
  icon,
  iconType,
  bgColor,
  textColor,
  faqs,
  onClickMore,
  sectionHelperText,
  helperTextColor,
  primaryAccentColor,
  secondaryAccentColor,
  categoriesHeadingTextColor,
  moreClickHref,
}) => {
  const navigate = useNavigate();
  const lightenedBorderColor = tinycolor(textColor).lighten(90).toString();
  const { language } = useSettings();

  const onClickFaq = (publish_id: string, id?: string) => {
    const faq = faqs.find(item => item.publish_id === publish_id);
    if (faq) {
      navigate(
        `/article/${language}/faq/${publish_id}-${faq?.faq_title.toLowerCase().replace(/\s+/g, '-')}`,
      );
      const formattedTitle = faq?.faq_title.toLowerCase().replace(/\s+/g, '-');
      appInsights.trackEvent({
        name: 'landingPage',
        properties: {
          buttonName: 'categoryTile',
          userName: 'anonymous',
          publishId: publish_id,
          articleTitle: formattedTitle,
          category: sectionName,
          articleId: id,
          sectionId: section_id,
          language,
        },
      });
    }
  };

  const faqHref = (publish_id: string) => {
    const faq = faqs.find(item => item.publish_id === publish_id);
    if (faq) {
      return `/article/${language}/faq/${publish_id}-${faq?.faq_title.toLowerCase().replace(/\s+/g, '-')}`;
    }
    // eslint-disable-next-line no-console
    console.log('section_id', section_id);
    return undefined;
  };

  return (
    <Box
      borderWidth="1px"
      // borderRadius="lg"
      overflow="hidden"
      // p="3"
      backgroundColor={bgColor}
      color={textColor}
      position="relative" // Ensures date can be positioned in bottom-right
      height="100%" // Ensure box takes full height for positioning
      boxShadow="md"
    >
      {/* Flexbox to align the title at the top */}
      <Flex direction="column" justify="space-between">
        <HStack
          cursor="pointer"
          align="center"
          borderBottom={`1px solid ${lightenedBorderColor}`}
          p={3}
          bgColor={primaryAccentColor}
          minH="5rem"
        >
          <Box>
            {iconType === 'svg' ? (
              <SectionIcon
                color={categoriesHeadingTextColor}
                svgPath={icon}
                // boxSize="16"
                height="40px"
                width="40px"
              />
            ) : (
              <Icon
                as={getIconFromString(icon) || FaRegQuestionCircle}
                boxSize="8"
                color={categoriesHeadingTextColor}
              />
            )}
          </Box>

          <a href={moreClickHref}>
            <Box
              as="span"
              cursor="pointer"
              onClick={(e: {
                button: number;
                metaKey: any;
                ctrlKey: any;
                shiftKey: any;
                altKey: any;
                preventDefault: () => void;
              }) => {
                const isLeftClick = e.button === 0;
                const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
                // SPA routing on normal click
                if (isLeftClick && !isModifiedClick) {
                  e.preventDefault();
                  onClickMore(sectionName.toLowerCase().replace(/\s+/g, '-'));
                }
              }}
            >
              <CustomText fontSize="lg" fontWeight="bold" color={categoriesHeadingTextColor}>
                {sectionName}
              </CustomText>
            </Box>
          </a>
        </HStack>
        {/* Map through faqs and slice based on a static limit of 3 */}
        {faqs?.slice(0, 3).map(faq => (
          <a href={faqHref(faq.publish_id)}>
            <HStack
              justify="space-between"
              align="center"
              borderBottom={`1px solid ${lightenedBorderColor}`}
              p={4}
              _hover={{ boxShadow: 'md', transform: 'scale(1.05)' }}
              key={faq.publish_id} // Changed to use publish_id as key
              onClick={e => {
                const isLeftClick = e.button === 0;
                const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
                // SPA routing on normal click
                if (isLeftClick && !isModifiedClick) {
                  e.preventDefault();
                  onClickFaq(faq.publish_id);
                }
              }}
            >
              {/* Push icon to the right */}
              <CustomText color={textColor} fontSize="sm" fontWeight="medium">
                {faq.faq_title}
              </CustomText>
              <ChevronRightIcon color={secondaryAccentColor} />
            </HStack>
          </a>
        ))}

        {/* Show "More" button if there are more than 3 faqs */}
        {faqs?.length > 3 && (
          <HStack justify="flex-end" p={4} mt="auto">
            <a href={moreClickHref}>
              <Button
                color={helperTextColor}
                onClick={e => {
                  const isLeftClick = e.button === 0;
                  const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
                  // SPA routing on normal click
                  if (isLeftClick && !isModifiedClick) {
                    e.preventDefault();
                    onClickMore(sectionName.toLowerCase().replace(/\s+/g, '-'));
                  }
                }}
                size="sm"
                variant="link"
                colorScheme="teal"
              >
                {sectionHelperText}
              </Button>
            </a>
            <ExternalLinkIcon color={secondaryAccentColor} />
          </HStack>
        )}
      </Flex>
    </Box>
  );
};
